import { DropdownItemRadio } from '@atlaskit/dropdown-menu';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import {
  sendSortEvent,
  SendDatabaseSortEventSources,
  useAnalytics,
} from '@databases/analytics';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { DatabaseLayoutType, DatabaseViewSettings } from '@databases/types/database/view';
import { ViewChangeTypes, getGroupField } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import useView from '../../hooks/useView';
import { messages } from '../../i18n';
import { getSortIcon } from '../../utils/utils';
import FieldsSearch from '../FieldsSearch';

import SortRowWrapper from './SortRowWrapper';

export interface ViewSettingsSortProps {
  contentId: string;
  databaseFields: DatabaseField[];
}

const ViewSettingsSort: FunctionComponent<ViewSettingsSortProps> = ({
  contentId,
  databaseFields,
}) => {
  const client = useAnalytics();
  const { formatMessage } = useIntl();
  const { view, setViewChange } = useView();
  const layout = view.settings.layout;
  const currentSort = view.settings.sort;

  const database = defaultStoreUtils.useEqualSelector((state) => {
    const database = contentId
      ? selectDatabaseByContentIdRequired(state, {
          contentId,
        })
      : undefined;
    return database;
  });
  const groupField = database && getGroupField(database, layout);

  const [searchValue, setSearchValue] = useState<string>('');

  const icon = useMemo(
    () => getSortIcon(currentSort?.ascending === true),
    [currentSort?.ascending]
  );

  const handleSortChange = useCallback(
    (sort: DatabaseViewSettings['sort']) => {
      const field = databaseFields.find((f) => f.id === sort?.fieldId);
      setViewChange({
        type: ViewChangeTypes.VIEW_SORT,
        sort,
      });

      sendSortEvent(
        client,
        sort,
        SendDatabaseSortEventSources.VIEW_PANEL_MENU,
        field ? field.type : undefined,
        contentId
      );
    },
    [client, contentId, databaseFields, setViewChange]
  );

  return (
    <Stack>
      <FieldsSearch searchValue={searchValue} setSearchValue={setSearchValue} />
      <Box>
        <SortRowWrapper showBorder={databaseFields.length > 0}>
          <DropdownItemRadio
            id="none"
            isSelected={!currentSort}
            onClick={() => handleSortChange(undefined)}
            testId={`sort-item-radio-no-sorting`}
          >
            {formatMessage(messages['view-settings-panel.no-sorting-option-text'])}
          </DropdownItemRadio>
        </SortRowWrapper>
        {databaseFields
          .filter(
            (field) =>
              (layout.type !== DatabaseLayoutType.BOARD || field.id !== groupField?.id) &&
              field.type !== DatabaseFieldType.ATTACHMENT
          )
          .filter((field) => field.name.toLowerCase().includes(searchValue.toLowerCase()))
          .map((field, key) => {
            const sortFieldChanged = field.id !== currentSort?.fieldId;
            const sortValue = !sortFieldChanged
              ? currentSort?.ascending
                ? 'ascending'
                : 'descending'
              : 'none';

            return (
              <SortRowWrapper
                key={field.id}
                showBorder={key !== databaseFields.length - 1}
              >
                <DropdownItemRadio
                  id={field.id}
                  isSelected={currentSort?.fieldId === field.id}
                  testId={`sort-item-radio-${key}`}
                  onClick={() => {
                    handleSortChange({
                      fieldId: field.id,
                      ascending: sortFieldChanged ? true : !currentSort?.ascending,
                    });
                  }}
                  aria-sort={sortValue}
                  aria-label={formatMessage(
                    messages['view-settings-panel.sorting-option-text'],
                    {
                      field: field.name,
                      sort: sortValue,
                    }
                  )}
                >
                  <Inline alignBlock="center" space="space.050">
                    <Text maxLines={1}>{field.name}</Text>
                    {currentSort?.fieldId === field.id && icon}
                  </Inline>
                </DropdownItemRadio>
              </SortRowWrapper>
            );
          })}
      </Box>
    </Stack>
  );
};

export default ViewSettingsSort;
